/**
 * views/SkuWorklist.tsx — suspected master Ti/Hi errors for a warehouse.
 *
 * Manager/admin panel. The pipeline never edits master SKU geometry itself
 * (policy): when a reconcile detects a whole-layer signature across many
 * pallets of one item, it withholds those results (N/A) and records the
 * evidence here — master Ti/Hi, the WMS-constant quantity, an example photo,
 * SOH reference and timestamps. The manager verifies against the photo,
 * corrects the master upload, and re-reconciles.
 */
import { useCallback, useEffect, useState } from 'react';

import { apiClient } from '@/api/client';
import { useAuthImage } from '@/hooks/useAuthImage';

interface SkuWorklistItem {
  id: string;
  sku_code: string;
  kind?: string;   // 'wrong_layers' | 'missing_master' (0053)
  description: string | null;
  cartons_per_layer: number | null;
  max_layers: number | null;
  calculated_boxes: number | null;
  wms_expected: number | null;
  suggested_layers: number | null;
  record_count: number;
  example_photo_id: string | null;
  example_photo_url: string | null;
  example_location: string | null;
  example_sscc: string | null;
  status: string;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

function EvidencePhoto({ url }: { url: string | null }) {
  const src = useAuthImage(url);
  const [zoom, setZoom] = useState(false);
  if (!url) return <span className="sku-wl-nophoto">no photo</span>;
  if (src === false) return <span className="sku-wl-nophoto">unavailable</span>;
  if (!src) return <span className="sku-wl-nophoto">loading…</span>;
  return (
    <>
      <img
        src={src}
        alt="evidence pallet"
        className="sku-wl-thumb"
        onClick={() => setZoom(true)}
        title="Click to enlarge"
      />
      {zoom && (
        <div className="sku-wl-lightbox" onClick={() => setZoom(false)}>
          <img src={src} alt="evidence pallet enlarged" />
        </div>
      )}
    </>
  );
}

interface Props {
  warehouseId: string;
  onBack: () => void;
}

export function SkuWorklist({ warehouseId, onBack }: Props) {
  const [items, setItems] = useState<SkuWorklistItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Download the worklist as an Excel workbook to send to the WMS owner.
  // Mirrors the current view (open-only vs include-resolved).
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const url = `/api/manage/sku-worklist/${warehouseId}/export.xlsx`
        + (showResolved ? '?include_resolved=true' : '');
      const res = await apiClient.getRaw(url);
      if (!res.ok) { alert(`Export failed (${res.status}).`); return; }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `SKU_Master_Errors_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      alert('Could not download — check your connection.');
    } finally {
      setExporting(false);
    }
  }, [warehouseId, showResolved]);

  const load = useCallback(async () => {
    try {
      const data = await apiClient.get<SkuWorklistItem[]>(
        `/api/manage/sku-worklist/${warehouseId}?include_resolved=${showResolved}`,
      );
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load SKU worklist.');
    }
  }, [warehouseId, showResolved]);

  useEffect(() => { load(); }, [load]);

  const resolve = useCallback(async (item: SkuWorklistItem) => {
    setBusy(item.id);
    try {
      await apiClient.patch(
        `/api/manage/sku-worklist/${item.id}/resolve?reopen=${item.status === 'resolved'}`, null,
      );
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Update failed.');
    } finally {
      setBusy(null);
    }
  }, [load]);

  return (
    <div className="sku-wl">
      <div className="sku-wl-toolbar">
        <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back to report</button>
        <h2 className="sku-wl-title">📦 SKU Worklist — suspected master Ti/Hi errors</h2>
        <label className="sku-wl-toggle">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={e => setShowResolved(e.target.checked)}
          />
          show resolved
        </label>
        <button
          className="btn btn--primary btn--sm"
          onClick={handleExport}
          disabled={exporting || !items || items.length === 0}
          title="Download the worklist as an Excel file to send to the customer"
        >
          {exporting ? 'Preparing…' : '⬇ Download Excel'}
        </button>
      </div>
      {error && <p className="sku-wl-error">⚠ {error}</p>}
      {items === null && !error && <p className="sku-wl-note">Loading…</p>}
      {items && items.length === 0 && (
        <p className="sku-wl-empty">✅ No suspected master errors for this warehouse.</p>
      )}

      {items && items.length > 0 && (
        <>
          <WorklistSection
            title="Wrong Ti/Hi — pallets disagree by whole layers"
            note={
              'Records with these SKUs were withheld from the report (N/A) because many '
              + 'pallets of the same item disagree with the master by whole layers. Verify '
              + 'each against the evidence photo, correct the SKU master upload, then re-run '
              + 'Reconcile — the N/A results will recompute with the corrected geometry.'
            }
            items={items.filter(it => (it.kind ?? 'wrong_layers') === 'wrong_layers')}
            busy={busy}
            onResolve={resolve}
          />
          <WorklistSection
            title="Missing master — SKU absent from the master file"
            note={
              'These SKUs have NO Ti/Hi in the master, so their pallets could not be counted '
              + 'and were withheld (N/A). ADD each SKU (with its Ti and Hi) to the master '
              + 'upload, then re-run Reconcile.'
            }
            items={items.filter(it => it.kind === 'missing_master')}
            missing
            busy={busy}
            onResolve={resolve}
          />
        </>
      )}
    </div>
  );
}

function WorklistSection({
  title, note, items, busy, onResolve, missing = false,
}: {
  title: string;
  note: string;
  items: SkuWorklistItem[];
  busy: string | null;
  onResolve: (it: SkuWorklistItem) => void;
  missing?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <section className="sku-wl-section">
      <h3 className="sku-wl-section-title">{title} <span className="sku-wl-count">({items.length})</span></h3>
      <p className="sku-wl-note">{note}</p>
      <table className="sku-wl-table">
        <thead>
          <tr>
            <th>SKU / Item</th>
            <th>Description</th>
            {!missing && <th>Master Ti×Hi</th>}
            {!missing && <th>Calc</th>}
            <th>WMS says</th>
            {!missing && <th>Suggests Hi</th>}
            <th>Pallets</th>
            <th>Example</th>
            <th>Evidence photo</th>
            <th>Seen</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.id} className={it.status === 'resolved' ? 'sku-wl-row--resolved' : ''}>
              <td className="sku-wl-mono">{it.sku_code}</td>
              <td>{it.description ?? '—'}</td>
              {!missing && (
                <td className="sku-wl-mono">{it.cartons_per_layer ?? '?'}×{it.max_layers ?? '?'}</td>
              )}
              {!missing && <td className="sku-wl-mono">{it.calculated_boxes ?? '—'}</td>}
              <td className="sku-wl-mono sku-wl-strong">{it.wms_expected ?? '—'}</td>
              {!missing && (
                <td className="sku-wl-mono">{it.suggested_layers != null ? `Hi=${it.suggested_layers}` : '—'}</td>
              )}
              <td>{it.record_count}</td>
              <td className="sku-wl-mono">
                {it.example_location ?? '—'}
                {it.example_sscc && <div className="sku-wl-sub">{it.example_sscc}</div>}
              </td>
              <td><EvidencePhoto url={it.example_photo_url} /></td>
              <td className="sku-wl-sub">
                {it.last_seen_at ? new Date(it.last_seen_at).toLocaleString() : '—'}
              </td>
              <td>
                <button
                  className="btn btn--secondary btn--sm"
                  disabled={busy === it.id}
                  onClick={() => onResolve(it)}
                >
                  {it.status === 'resolved' ? 'Reopen' : 'Resolve'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
